/**
 * scheduleCommit.ts
 * Frontend service for the Commit Schedule API.
 *
 * All requests go through apiClient (MSAL auth + error capture).
 * No raw fetch() calls — see LESSONS-LEARNED.md for why direct fetch 404s in dev.
 *
 * Endpoints:
 *   POST /api/schedule/commit      — Phase 3, Item 10: flip all Tentative → Committed
 *                                    for a single Quote or WorkOrder source.
 *   POST /api/schedule/commit-bulk — Follow-up 1.5: flip explicitly-keyed rows
 *                                    (Master Gantt multi-select bulk commit).
 *   GET  /api/schedule/commit-check      — G2-1 (audit #2): read-only pre-commit
 *                                    check for the source doors (advisory bullets).
 *   POST /api/schedule/commit-bulk-check — G2-1: same, scoped to selected keys.
 */

import apiClient from './apiClient';
import type { SubmitCheckResult } from './scheduleScenario';

export type CommitSource = 'Quote' | 'WorkOrder';

export interface CommitSchedulePayload {
  source: CommitSource;
  sourceKey: string;
}

/** W2-6: the source's own draft plan that a commit submitted + locked. */
export interface CommitScenarioRef {
  scenarioKey: string;
  code: string;
  name: string;
}

export interface CommitScheduleResult {
  tasksTouched: number;
  bookingsCommitted: number;
  assignmentsCommitted: number;
  /** Index Tests 4.3: pooled IndexAllocation rows flipped Tentative→Committed. */
  indexAllocationsCommitted?: number;
  /**
   * W2-6 (Option B): the source's own draft plan that this commit also
   * committed and locked (Status='Submitted'), or null when the source had no
   * single active draft. Hand-mirrored from backend CommitResult — keep in
   * lockstep (tsc catches neither direction).
   */
  scenarioSubmitted: CommitScenarioRef | null;
  /** Codes of active draft plans NOT auto-committed (only when the source has 2+). */
  draftsSkipped: string[];
}

/**
 * Commit the Tentative MachineBookings and EmployeeAssignments of a Quote or
 * WorkOrder, flipping their Status to 'Committed'.
 *
 * W2-6 scope: live-board (BASELINE) rows on the source's tasks PLUS — when the
 * source has exactly one active draft plan — that whole plan's rows; the plan
 * is then marked Submitted/locked. Other scenarios' rows are never touched.
 *
 * Idempotent: calling this a second time on an already-committed source
 * returns { tasksTouched: N, bookingsCommitted: 0, assignmentsCommitted: 0 }.
 *
 * @param payload  { source: 'Quote'|'WorkOrder', sourceKey: UUID }
 * @returns        Counts of tasks touched, bookings and assignments committed.
 */
export async function commitSchedule(
  payload: CommitSchedulePayload,
): Promise<CommitScheduleResult> {
  const response = await apiClient.post<CommitScheduleResult>(
    '/schedule/commit',
    payload,
  );
  return response.data;
}

/**
 * Status counts for the Commit Schedule button on Quote/WO pages.
 *
 * W2-6: tentative counts match exactly what commitSchedule will flip
 * (live-board BASELINE rows + the single active draft plan's rows, if any);
 * committed counts span all scenarios (Committed = live regardless of plan).
 * draftScenarios lists the source's active draft plans — exactly one means
 * commit will also submit + lock it; two or more means none is auto-committed.
 */
export interface CommitStatusSummary {
  tentativeBookings: number;
  committedBookings: number;
  tentativeAssignments: number;
  committedAssignments: number;
  totalTasks: number;
  draftScenarios: CommitScenarioRef[];
}

/**
 * GET /api/schedule/commit-status?source=...&sourceKey=...
 * Returns Tentative vs Committed counts so the UI knows whether to
 * enable, disable, or relabel the Commit Schedule button.
 */
export async function fetchCommitStatus(
  payload: CommitSchedulePayload,
): Promise<CommitStatusSummary> {
  const response = await apiClient.get<CommitStatusSummary>(
    `/schedule/commit-status?source=${payload.source}&sourceKey=${encodeURIComponent(payload.sourceKey)}`,
  );
  return response.data;
}

/**
 * GET /api/schedule/commit-check?source=...&sourceKey=...
 *
 * G2-1 (audit #2): read-only pre-commit check for the Quote/WO "Commit
 * Schedule" doors — the SAME advisory categories the planning page shows
 * before Finalize, scoped to exactly what commitSchedule will flip (the
 * source's live-board rows + its single active draft plan, W2-6). Render with
 * the shared buildSubmitWarnings; the dialog stays confirmable (advisory).
 */
export async function fetchCommitCheck(
  payload: CommitSchedulePayload,
): Promise<SubmitCheckResult> {
  const response = await apiClient.get<SubmitCheckResult>(
    `/schedule/commit-check?source=${payload.source}&sourceKey=${encodeURIComponent(payload.sourceKey)}`,
  );
  return response.data;
}

// ---------------------------------------------------------------------------
// Bulk Commit — Follow-up 1.5 (Master Gantt multi-select)
// ---------------------------------------------------------------------------

export interface BulkCommitPayload {
  machineBookingKeys: string[];
  employeeAssignmentKeys: string[];
}

export interface BulkCommitResult {
  bookingsCommitted: number;
  assignmentsCommitted: number;
}

/**
 * Commit a set of explicitly-keyed MachineBookings and EmployeeAssignments.
 *
 * Wraps POST /api/schedule/commit-bulk.
 * Idempotent: already-Committed keys return 0 counts.
 *
 * @param payload  Keys collected from the Master Gantt selection set.
 */
export async function commitScheduleBulk(
  payload: BulkCommitPayload,
): Promise<BulkCommitResult> {
  const response = await apiClient.post<BulkCommitResult>(
    '/schedule/commit-bulk',
    payload,
  );
  return response.data;
}

/**
 * POST /api/schedule/commit-bulk-check
 *
 * G2-1 (audit #2): read-only pre-commit check for the Master Gantt bulk
 * commit, scoped to the selected keys (unstaffed bookings, day-off conflicts,
 * hard machine conflicts, overloaded weeks). POST only because the key lists
 * don't fit a query string — the endpoint performs no writes. Advisory: the
 * confirm dialog renders the bullets and stays confirmable.
 */
export async function checkBulkCommit(
  payload: BulkCommitPayload,
): Promise<SubmitCheckResult> {
  const response = await apiClient.post<SubmitCheckResult>(
    '/schedule/commit-bulk-check',
    payload,
  );
  return response.data;
}
